/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type React from 'react';
import { ImageOff } from 'lucide-react';
import { Link } from '@/components/link';
import { DynamicImage } from '@/components/dynamic-image';
import { useAnalytics } from '@/hooks/use-analytics';
import { toImageUrl } from '@/lib/images/dynamic-image';
import { resolveSearchSuggestionImageSrc } from '@/lib/search/suggestion-images';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/lib/currency';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import DynamicImageProvider from '@/providers/dynamic-image';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { useIsDesktop } from '@/hooks/use-is-desktop';
import { cn } from '@/lib/utils';
import type { AppConfig } from '@/types/config';

/**
 * Responsive DIS widths for suggestion tiles:
 * mobile/tablet ≈ 2/3 viewport (peek next card), desktop ≈ 1/5 of the products column.
 */
const suggestionProductImageWidths = ['66vw', '66vw', '170px', '170px'];

interface Suggestion {
    name: string;
    link: string;
    image?: string;
    price?: number;
    brand?: string;
}

interface SearchSuggestionsPopupProps {
    suggestions?: Suggestion[];
    searchPhrase?: string;
    closeAndNavigate?: (link: string) => void;
}

interface SuggestionTileProps {
    suggestion: Suggestion;
    config: AppConfig;
    currency: string;
    language: string;
    productImageAlt: string;
    noImageAvailable: string;
    onSelect: (suggestion: Suggestion) => void;
    className?: string;
}

function SuggestionTile({
    suggestion,
    config,
    currency,
    language,
    productImageAlt,
    noImageAvailable,
    onSelect,
    className,
}: SuggestionTileProps) {
    return (
        <Link
            data-testid="product-tile"
            to={suggestion.link}
            onMouseDown={() => onSelect(suggestion)}
            className={cn('group block w-full', className)}>
            <div className="w-full">
                <div className="mb-4">
                    <div className="relative aspect-[4/5] w-full">
                        {suggestion.image ? (
                            <DynamicImage
                                src={
                                    toImageUrl({
                                        src: resolveSearchSuggestionImageSrc(suggestion.image, config),
                                        config,
                                    }) ?? ''
                                }
                                alt={suggestion.name || productImageAlt}
                                widths={suggestionProductImageWidths}
                                imageProps={{
                                    className: 'absolute inset-0 block h-full w-full object-cover',
                                }}
                            />
                        ) : (
                            <div className="absolute inset-0 flex h-full w-full items-center justify-center bg-header-menu-active-background text-header-menu-foreground">
                                <div className="text-center">
                                    <ImageOff className="mx-auto mb-1 h-8 w-8" />
                                    <div className="text-xs">{noImageAvailable}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {suggestion.brand && (
                    <p className="w-full pb-2 text-sm font-normal capitalize leading-none text-muted-foreground">
                        {suggestion.brand}
                    </p>
                )}

                <p className="w-full pb-2 text-base font-semibold leading-6 text-card-foreground group-hover:underline">
                    {suggestion.name}
                </p>

                {suggestion.price ? (
                    <p className="text-base font-semibold leading-none text-foreground">
                        {formatCurrency(suggestion.price, language, currency)}
                    </p>
                ) : null}
            </div>
        </Link>
    );
}

const SearchSuggestionsPopup: React.FC<SearchSuggestionsPopupProps> = ({
    suggestions,
    searchPhrase,
    closeAndNavigate,
}) => {
    const analytics = useAnalytics();
    const config = useConfig();
    const { t, i18n } = useTranslation('common');
    const { currency } = useSite();
    const isDesktop = useIsDesktop();

    if (!suggestions || suggestions.length === 0) {
        return null;
    }

    const handleClick = (suggestion: Suggestion) => {
        void analytics.trackClickSearchSuggestion({
            searchInputText: searchPhrase || '',
            suggestion: suggestion.name,
        });
        if (closeAndNavigate) {
            closeAndNavigate(suggestion.link);
        }
    };

    const tileProps = {
        config,
        currency,
        language: i18n.language,
        productImageAlt: t('productImageAlt'),
        noImageAvailable: t('noImageAvailable'),
        onSelect: handleClick,
    };

    return (
        <div data-testid="sf-horizontal-product-suggestions" className="overflow-hidden">
            <DynamicImageProvider value={{ widths: suggestionProductImageWidths }}>
                {isDesktop ? (
                    <div className="flex gap-4">
                        {suggestions.map((suggestion) => (
                            <SuggestionTile
                                key={suggestion.link}
                                suggestion={suggestion}
                                {...tileProps}
                                className="min-w-0 basis-[calc((100%-4rem)/5)] shrink-0 grow-0"
                            />
                        ))}
                    </div>
                ) : (
                    <Carousel className="w-full" opts={{ align: 'start', dragFree: true, containScroll: 'trimSnaps' }}>
                        <CarouselContent className="ml-0 gap-4">
                            {suggestions.map((suggestion) => (
                                <CarouselItem key={suggestion.link} className="basis-2/3 p-0">
                                    <SuggestionTile suggestion={suggestion} {...tileProps} />
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                    </Carousel>
                )}
            </DynamicImageProvider>
        </div>
    );
};

export default SearchSuggestionsPopup;
